/**
 * Source Service
 *
 * Handles API calls to the ai-hybridRAG-server for source-related operations,
 * primarily URL refresh for expired SAS tokens.
 *
 * Endpoints:
 * - GET /api/sources/{file_id}/url - Refresh URL for a single source
 * - POST /api/sources/urls/refresh - Refresh URLs for multiple sources
 */

import { normalizeRagUrl } from '@/utils/ragUrl'

/**
 * Response from the URL refresh endpoint
 */
export interface RefreshedUrl {
  file_id: string
  // Nullable for the same reason as RAGSource: the server reports
  // url: null (with an error) when it cannot resolve the source file.
  storage_url: string | null
  storage_url_type: string | null
  storage_url_expires_at: string | null
}

/**
 * Batch refresh request body
 */
interface BatchRefreshRequest {
  file_ids: string[]
}

/**
 * Batch refresh response
 */
interface BatchRefreshResponse {
  urls: RefreshedUrl[]
}

class SourceService {
  private baseUrl: string

  constructor() {
    this.baseUrl = ''
  }

  /**
   * Set the base URL for the RAG server.
   *
   * Goes through `normalizeRagUrl` so that absolute URLs to the same
   * Container Apps environment get rewritten to a same-origin
   * `/rag-proxy/<instance>` path (matches RagConnector behavior; avoids
   * cross-origin CORS preflights).
   */
  setBaseUrl(url: string): void {
    this.baseUrl = normalizeRagUrl(url)
  }

  /**
   * Get the current base URL
   */
  getBaseUrl(): string {
    return this.baseUrl
  }

  /**
   * Refresh URL for a single source
   * @param fileId - The file_id of the source to refresh
   * @returns The refreshed URL information
   */
  async refreshSourceUrl(fileId: string): Promise<RefreshedUrl> {
    if (!this.baseUrl) {
      throw new Error('Source service base URL not configured')
    }

    const response = await fetch(
      `${this.baseUrl}/api/sources/${encodeURIComponent(fileId)}/url`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      }
    )

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(
        `Failed to refresh URL: ${response.status} - ${errorText}`
      )
    }

    return response.json()
  }

  /**
   * Refresh URLs for multiple sources in a batch
   * @param fileIds - Array of file_ids to refresh
   * @returns Array of refreshed URL information
   */
  async refreshSourceUrls(fileIds: string[]): Promise<RefreshedUrl[]> {
    if (!this.baseUrl) {
      throw new Error('Source service base URL not configured')
    }

    if (fileIds.length === 0) {
      return []
    }

    const requestBody: BatchRefreshRequest = { file_ids: fileIds }

    const response = await fetch(`${this.baseUrl}/api/sources/urls/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(
        `Failed to refresh URLs: ${response.status} - ${errorText}`
      )
    }

    const data: BatchRefreshResponse | RefreshedUrl[] = await response.json()

    // Handle both array response and { urls: [...] } response format
    if (Array.isArray(data)) {
      return data
    }
    return data.urls || []
  }

  /**
   * Check if a URL is expired
   * @param expiresAt - ISO timestamp of expiration; null/absent = never expires
   * @returns True if the URL is expired
   */
  isUrlExpired(expiresAt: string | null): boolean {
    if (!expiresAt) return false
    try {
      const expirationTime = new Date(expiresAt).getTime()
      return Date.now() > expirationTime
    } catch {
      return false
    }
  }

  /**
   * Check if a URL will expire within the specified buffer time
   * @param expiresAt - ISO timestamp of expiration
   * @param bufferMinutes - Buffer time in minutes (default: 5)
   * @returns True if the URL will expire soon
   */
  isUrlExpiringSoon(expiresAt: string | null, bufferMinutes: number = 5): boolean {
    if (!expiresAt) return false
    try {
      const expirationTime = new Date(expiresAt).getTime()
      const bufferMs = bufferMinutes * 60 * 1000
      return Date.now() > expirationTime - bufferMs
    } catch {
      return false
    }
  }
}

// Singleton instance
let sourceServiceInstance: SourceService | null = null

/**
 * Get the singleton SourceService instance
 */
export function getSourceService(): SourceService {
  if (!sourceServiceInstance) {
    sourceServiceInstance = new SourceService()
  }
  return sourceServiceInstance
}

/**
 * Initialize the SourceService with a base URL
 */
export function initSourceService(baseUrl: string): SourceService {
  const service = getSourceService()
  service.setBaseUrl(baseUrl)
  return service
}
